import type { QuantityUnit } from "../domain/feature";
import type { TradeId } from "../domain/trade";

/**
 * WHAT A CONTRACTOR SHOULD ACTUALLY READ (spec §76C).
 *
 * The renovation model knows that baseboard is being replaced in eleven
 * separate rooms, and it must keep knowing it — every one of those rooms is
 * a feature with a quantity, an assembly, a cost and, later, an actual.
 * But a carpenter handed eleven identical lines reads noise, not scope.
 *
 * This engine collapses that repetition into contractor language. It is
 * DETERMINISTIC — no model, no API, no randomness — and it INVENTS NOTHING:
 * every noun it prints comes from work the project actually contains, and
 * every number from a quantity somebody recorded.
 *
 * Three rules hold it together:
 *
 *   1. GENERALIZE REPETITION, NEVER MEANING. Eleven rooms of baseboard is
 *      repetition. A staircase, a panel upgrade, a beam or a manual item is
 *      meaning, and stays on the page in its own words (§76F).
 *   2. LOCATION SURVIVES WHEN IT MATTERS. Three rooms are named; nine are
 *      counted; effectively-everywhere says so (§76D).
 *   3. PRESENTATION ONLY. This changes what is printed and nothing else —
 *      the scope a package covers is its feature ids, which no sentence
 *      here can alter (§76N).
 */

/* ------------------------------------------------------------------ *
 * Inputs
 * ------------------------------------------------------------------ */

export interface SummaryItem {
  featureId: string;
  featureDefinitionId: string | null;
  /** The feature's own name, e.g. "Replace Baseboard". */
  name: string;
  action: string;
  roomId: string | null;
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  /** True for work the user wrote by hand (§76Q). */
  manual: boolean;
}

export interface SummaryRoom {
  roomId: string;
  roomName: string;
  floorName: string | null;
}

export interface SummaryInput {
  tradeId: TradeId;
  items: readonly SummaryItem[];
  /** Every room in the project — the denominator for "throughout". */
  rooms: readonly SummaryRoom[];
}

/* ------------------------------------------------------------------ *
 * Output
 * ------------------------------------------------------------------ */

export interface SummaryGroup {
  /** Stable key for the collapsed work: definition + action. */
  key: string;
  /** "baseboards", "toilets" — the plural noun as printed. */
  noun: string;
  action: string;
  /** The instruction word actually printed ("Replace", "Paint"). */
  verb: string;
  featureIds: string[];
  roomIds: string[];
  roomNames: string[];
  /** The location language chosen for this group. */
  scope: "whole-home" | "named-rooms" | "counted-rooms" | "project-level";
  quantity: { unit: QuantityUnit; valueX100: number } | null;
  /** True when this group was kept explicit rather than folded in (§76F). */
  important: boolean;
}

export interface ScopeSummary {
  /** The paragraph a contractor reads. */
  sentences: string[];
  /** Work that stays its own bullet: manual items and anything unusual. */
  bullets: string[];
  /** What was collapsed, for the owner-facing expansion (§76J). */
  groups: SummaryGroup[];
  /** The whole summary as one printable block. */
  text: string;
}

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

const ACTION_VERB: Readonly<Record<string, string>> = {
  replace: "Replace",
  install: "Install",
  remove: "Remove",
  repair: "Repair",
  refinish: "Refinish",
  relocate: "Relocate",
  build: "Build",
  demolish: "Demolish",
};

/**
 * Words a work item's own name may already open with. Every seeded
 * definition is phrased as an instruction, and the instruction it uses is
 * not always the one its ACTION implies: closing a door opening is filed
 * under 'remove', building a wall under 'install'. Prepending the action's
 * verb to a name that already carries one produced "Remove close door
 * opening" and "Install build interior walls" — so a name that already
 * reads as an instruction is left exactly as its author wrote it.
 */
export const INSTRUCTION_WORDS: ReadonlySet<string> = new Set([
  ...Object.values(ACTION_VERB).map((v) => v.toLowerCase()),
  "close", "construct", "reface", "level", "paint", "seal", "gut",
  "demo", "tear", "haul", "frame", "patch", "finish", "clean", "rebuild",
]);

/** True when a name already tells somebody what to do. */
export function readsAsInstruction(name: string): boolean {
  const first = name.trim().split(/\s+/)[0] ?? "";
  return INSTRUCTION_WORDS.has(first.toLowerCase());
}

/**
 * Nouns worth stating properly. Everything else is derived from the
 * feature's own name, which the seeded definitions already phrase well
 * ("Replace Baseboard" → "baseboards").
 */
const NOUN: Readonly<Record<string, string>> = {
  "replace-hvac": "HVAC system",
  "replace-electrical-panel": "electrical panel",
  "whole-house-repipe": "plumbing supply piping",
  "replace-water-heater": "water heater",
  "install-staircase": "staircase",
  "replace-staircase": "staircase",
  "refinish-stairs": "stair finish",
  "replace-treads-risers": "stair treads and risers",
  "repair-stair-structure": "stair structure",
  "replace-beams": "structural beams",
  "repair-joists": "floor joists",
  "repair-subfloor-structure": "subfloor structure",
  "foundation-repair": "foundation",
  "repair-slab-foundation": "slab foundation",
  "repair-pier-and-beam": "pier-and-beam foundation",
  "level-foundation": "foundation level",
  "install-piers": "foundation piers",
  "structural-repair": "structural repairs",
  "replace-roof": "roof",
  "replace-roof-decking": "roof decking",
  "replace-outlets-switches": "outlets and switches",
  "replace-light-fixture": "light fixtures",
  "install-ceiling-fan": "ceiling fans",
  "install-smoke-detectors": "smoke detectors",
  "replace-vanity-light": "vanity lights",
  "replace-exhaust-fan": "exhaust fans",
  "replace-kitchen-sink": "kitchen sink",
  "replace-kitchen-faucet": "kitchen faucet",
  "replace-bathroom-faucet": "bathroom faucet",
  "replace-garbage-disposal": "garbage disposal",
  "replace-toilet": "toilets",
  "replace-tub": "tubs",
  "replace-shower": "showers",
  "replace-vanity": "vanities",
  "replace-vanity-top": "vanity tops",
  "replace-cabinets": "cabinetry",
  "reface-cabinets": "cabinet fronts",
  "replace-countertops": "countertops",
  "replace-flooring": "flooring",
  "refinish-hardwood": "hardwood floors",
  "replace-baseboard": "baseboards",
  "install-crown-molding": "crown molding",
  "paint-walls": "interior walls",
  "paint-ceiling": "ceilings",
  "repair-drywall": "drywall",
  "install-insulation": "insulation",
  "replace-window": "windows",
  "install-window": "windows",
  "remove-window-opening": "windows",
  "replace-interior-door": "interior doors",
  "install-interior-door": "interior doors",
  "replace-exterior-door": "exterior doors",
  "remove-door-opening": "doors",
  "replace-garage-door": "garage door",
  "build-interior-wall": "interior walls",
  "construct-interior-wall": "interior walls",
  "remove-interior-wall": "interior walls",
  "gut-room": "existing finishes",
  "remove-fixture": "fixtures",
  "install-backsplash": "backsplash tile",
  "install-tile-floor": "floor tile",
  "install-shower-tile": "shower tile",
  "install-tub-shower-surround": "tub and shower surrounds",
  "replace-bathroom-mirror": "bathroom mirrors",
  "replace-dishwasher": "dishwasher connections",
  "replace-refrigerator": "refrigerator water lines",
  "install-washer": "washer connections",
  "install-dryer": "dryer vents and circuits",
  "replace-range": "range circuits",
  "replace-range-hood": "range hood venting",
  "replace-microwave": "microwave circuits",
  "replace-siding": "siding",
  "replace-gutters": "gutters",
  "install-gutters": "gutters",
};

/**
 * The instruction word. A known action decides it; otherwise the work's own
 * name does ("Paint Walls" → "Paint"), because the seeded definitions are
 * already phrased as instructions. Only a name that says nothing falls back
 * to "Complete".
 */
function verbFor(item: SummaryItem): string {
  const known = ACTION_VERB[item.action];
  if (known) return known;
  const first = item.name.trim().split(/\s+/)[0] ?? "";
  if (first.length > 2 && /^[A-Za-z]+$/.test(first)) {
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  return "Complete";
}

/** "Replace Baseboard" → "baseboard": the noun without its instruction. */
function nounFromName(name: string, action: string): string {
  const verb = ACTION_VERB[action] ?? name.trim().split(/\s+/)[0];
  let text = name.trim();
  if (verb && text.toLowerCase().startsWith(`${verb.toLowerCase()} `)) {
    text = text.slice(verb.length + 1);
  }
  return text.trim().toLowerCase() || name.trim().toLowerCase();
}

/** Plain English pluralisation, enough for the nouns this domain uses. */
function plural(noun: string): string {
  if (/\b(system|panel|piping|foundation|roof|structure|level|finish|insulation|drywall|tile|siding|cabinetry|flooring|molding)\b$/.test(noun)) {
    return noun;
  }
  if (/(s|x|ch|sh)$/.test(noun)) return `${noun}es`;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/s$/.test(noun)) return noun;
  return `${noun}s`;
}

function nounFor(item: SummaryItem): string {
  const stated = item.featureDefinitionId ? NOUN[item.featureDefinitionId] : undefined;
  if (stated) return stated;
  return plural(nounFromName(item.name, item.action));
}

/** "the Kitchen, Dining Room and Living Room" */
function joinNames(names: readonly string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  // A member that already contains "and" needs the serial comma, or the
  // list reads as one run-on phrase.
  const serial = names.some((name) => name.includes(" and "));
  if (names.length === 2) return serial ? `${names[0]}, and ${names[1]}` : `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}${serial ? "," : ""} and ${names[names.length - 1]}`;
}

/** "1,850 sq ft" — from an integer × 100, never a float. */
const UNIT_WORD: Readonly<Record<string, string>> = {
  sqft: "sq ft",
  lf: "linear feet",
  sqyd: "sq yd",
  square: "squares",
  room: "rooms",
  hour: "hours",
  day: "days",
  each: "",
};

export function formatSummaryQuantity(unit: QuantityUnit, valueX100: number): string {
  const value = valueX100 / 100;
  const number = Number.isInteger(value) ? value.toLocaleString("en-US") : value.toLocaleString("en-US", { maximumFractionDigits: 1 });
  const word = UNIT_WORD[unit] ?? unit;
  return word ? `${number} ${word}` : `${number}`;
}

/* ------------------------------------------------------------------ *
 * Location language (spec §76D)
 * ------------------------------------------------------------------ */

/** Effectively everywhere: this many rooms AND this share of the house. */
export const WHOLE_HOME_MIN_ROOMS = 4;
export const WHOLE_HOME_SHARE_X100 = 70;
/** Up to this many rooms are worth naming; beyond it they are counted. */
export const NAMED_ROOM_LIMIT = 3;

function locationPhrase(group: SummaryGroup): string {
  switch (group.scope) {
    case "project-level":
      return "";
    case "whole-home":
      return "throughout the home";
    case "named-rooms":
      return `in the ${joinNames(group.roomNames)}`;
    case "counted-rooms":
      return `in ${group.roomIds.length} rooms`;
  }
}

/* ------------------------------------------------------------------ *
 * Work that never gets folded away (spec §76F)
 * ------------------------------------------------------------------ */

/**
 * Scope a contractor must see stated, not summarised into a category.
 * Collapsing a staircase into "associated finish carpentry" is how a bid
 * comes back missing the most expensive thing on the page.
 */
export const ALWAYS_EXPLICIT: ReadonlySet<string> = new Set([
  "install-staircase",
  "replace-staircase",
  "repair-stair-structure",
  "replace-treads-risers",
  "replace-beams",
  "repair-joists",
  "repair-subfloor-structure",
  "structural-repair",
  "foundation-repair",
  "repair-slab-foundation",
  "repair-pier-and-beam",
  "level-foundation",
  "install-piers",
  "replace-electrical-panel",
  "whole-house-repipe",
  "replace-hvac",
  "replace-water-heater",
  "replace-roof",
  "replace-roof-decking",
  "replace-siding",
  "build-interior-wall",
  "construct-interior-wall",
  "remove-interior-wall",
  "gut-room",
]);

const isExplicit = (item: SummaryItem): boolean =>
  item.manual || item.featureDefinitionId === null || ALWAYS_EXPLICIT.has(item.featureDefinitionId);

/* ------------------------------------------------------------------ *
 * Category language (spec §76E)
 * ------------------------------------------------------------------ */

/**
 * Trades whose repetitive work reads better as one sentence naming the
 * KINDS of work present than as a sentence per kind. Only the kinds a
 * project actually contains are ever named.
 */
interface AggregateStyle {
  lead: string;
  /** How the enumerated nouns are introduced. */
  connector: string;
}

export const AGGREGATE: Partial<Record<TradeId, AggregateStyle>> = {
  demolition: {
    lead: "Complete demolition required for the proposed renovation",
    connector: "including removal of designated",
  },
  plumbing: {
    lead: "Complete plumbing work for the renovation",
    connector: "including designated",
  },
  electrical: {
    lead: "Complete electrical work shown in the renovation scope",
    connector: "including designated",
  },
  "painting-interior": {
    lead: "Paint the designated interior surfaces",
    connector: "including",
  },
  drywall: {
    lead: "Complete drywall work shown in the renovation scope",
    connector: "including",
  },
  tile: {
    lead: "Complete tile work shown in the renovation scope",
    connector: "including",
  },
  cleaning: {
    lead: "Complete cleaning for the renovation",
    connector: "including",
  },
};

/* ------------------------------------------------------------------ *
 * The summariser
 * ------------------------------------------------------------------ */

function groupKey(item: SummaryItem): string {
  return `${item.featureDefinitionId ?? `name:${item.name.trim().toLowerCase()}`}|${item.action}`;
}

function buildGroups(input: SummaryInput): SummaryGroup[] {
  const roomName = new Map(input.rooms.map((room) => [room.roomId, room.roomName]));
  const totalRooms = input.rooms.length;
  const collected = new Map<string, { items: SummaryItem[]; order: number }>();
  let order = 0;
  for (const item of input.items) {
    const key = groupKey(item);
    const existing = collected.get(key);
    if (existing) existing.items.push(item);
    else collected.set(key, { items: [item], order: order++ });
  }

  const groups: SummaryGroup[] = [];
  for (const [key, entry] of collected) {
    const items = entry.items;
    const roomIds = [...new Set(items.map((i) => i.roomId).filter((id): id is string => id !== null))];
    const names = roomIds.map((id) => roomName.get(id) ?? "").filter((name) => name !== "");

    let scope: SummaryGroup["scope"];
    if (roomIds.length === 0) {
      scope = "project-level";
    } else if (
      roomIds.length >= WHOLE_HOME_MIN_ROOMS &&
      totalRooms > 0 &&
      roomIds.length * 100 >= totalRooms * WHOLE_HOME_SHARE_X100
    ) {
      scope = "whole-home";
    } else if (roomIds.length <= NAMED_ROOM_LIMIT) {
      scope = "named-rooms";
    } else {
      scope = "counted-rooms";
    }

    // A QUANTITY IS ONLY PRINTED WHEN IT IS AUTHORITATIVE: every piece of
    // the work must state one, in the same unit. A partial figure would
    // read as the total and under-price the job.
    const units = new Set(items.map((i) => i.quantityUnit).filter((u): u is QuantityUnit => u !== null));
    const everyoneStated = items.every((i) => i.quantityX100 !== null && i.quantityUnit !== null);
    const quantity =
      everyoneStated && units.size === 1
        ? {
            unit: [...units][0],
            valueX100: items.reduce((sum, i) => sum + (i.quantityX100 ?? 0), 0),
          }
        : null;

    groups.push({
      key,
      noun: nounFor(items[0]),
      action: items[0].action,
      verb: verbFor(items[0]),
      featureIds: items.map((i) => i.featureId),
      roomIds,
      roomNames: names,
      scope,
      quantity,
      important: items.some(isExplicit),
    });
  }
  return groups;
}

/** "Replace baseboards throughout the home." */
function sentenceFor(group: SummaryGroup): string {
  const verb = group.verb;
  const where = locationPhrase(group);
  const quantity =
    group.quantity && group.quantity.unit !== "each"
      ? ` (approximately ${formatSummaryQuantity(group.quantity.unit, group.quantity.valueX100)})`
      : "";
  // A COUNT ONLY WHERE IT ADDS SOMETHING: "7 baseboards throughout the
  // home" says less than "baseboards throughout the home", and the room
  // count already carries the scale wherever rooms are counted.
  const count =
    group.quantity &&
    group.quantity.unit === "each" &&
    group.quantity.valueX100 >= 200 &&
    (group.scope === "named-rooms" || group.scope === "project-level")
      ? `${formatSummaryQuantity("each", group.quantity.valueX100)} `
      : "";
  const body = `${verb} ${count}${group.noun}${where ? ` ${where}` : ""}${quantity}`;
  return `${body}.`;
}

export function summarizeScope(input: SummaryInput): ScopeSummary {
  const groups = buildGroups(input);
  if (groups.length === 0) {
    return { sentences: [], bullets: [], groups, text: "" };
  }

  const bullets: string[] = [];
  const sentences: string[] = [];

  const manual = input.items.filter((i) => i.manual);
  const explicitGroups = groups.filter((g) => g.important);
  const ordinary = groups.filter((g) => !g.important);

  const aggregate = AGGREGATE[input.tradeId];
  if (aggregate && ordinary.length >= 2) {
    // ONE sentence naming only the kinds of work this project contains.
    const nouns = [...new Set(ordinary.map((g) => g.noun))];
    const everywhere = ordinary.every((g) => g.scope === "whole-home");
    sentences.push(
      `${aggregate.lead}, ${aggregate.connector} ${joinNames(nouns)}${everywhere ? " throughout the home" : ""}.`,
    );
    // A quantity that IS authoritative still earns its place.
    for (const group of ordinary) {
      if (group.quantity && group.quantity.unit !== "each") {
        sentences.push(
          `${group.noun.charAt(0).toUpperCase()}${group.noun.slice(1)}: approximately ${formatSummaryQuantity(group.quantity.unit, group.quantity.valueX100)}.`,
        );
      }
    }
  } else {
    for (const group of ordinary) sentences.push(sentenceFor(group));
  }

  // Work that must stay explicit reads in its own words, after the
  // generalised paragraph so the contractor cannot miss it.
  for (const group of explicitGroups) {
    if (manual.some((m) => group.featureIds.includes(m.featureId))) continue;
    sentences.push(sentenceFor(group));
  }

  // A manual item is somebody's own words about work the model does not
  // know: it is never absorbed into a sentence the engine wrote (§76Q).
  for (const item of manual) {
    bullets.push(item.name.trim());
  }

  return {
    sentences,
    bullets,
    groups,
    text: [sentences.join(" "), ...bullets.map((b) => `• ${b}`)].filter((part) => part !== "").join("\n"),
  };
}
